import { Download, Printer } from 'lucide-react';
import { ApiError } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Button } from '@platform/design-system';
import type { ShippingClient } from '@platform/openapi';
import { usePageContext } from '../usePageContext.ts';
import { ORDERS_DISPATCH_ROLE } from './orders.ts';
import styles from './Orders.module.css';
import { useShipmentLabel } from './useShipmentLabel.ts';

export type LabelDownloadProps = {
  shipping: ShippingClient;
  shipmentId: string;
  onOpenSettings: () => void;
};

const SENDER_MISSING = 'SHIPPING_SENDER_MISSING';

export const LabelDownload = ({ shipping, shipmentId, onOpenSettings }: LabelDownloadProps) => {
  const { t, describeError } = usePageContext();
  const canPrint = useAuth().hasRole(ORDERS_DISPATCH_ROLE);
  const label = useShipmentLabel(shipping);
  if (!canPrint) return null;
  const senderMissing = label.error instanceof ApiError && label.error.code === SENDER_MISSING;
  const failure = label.error instanceof ApiError ? describeError(label.error) : t('orders.labelPrintFailed');

  return (
    <div className={styles['labelAction']}>
      <span className={styles['labelButtons']}>
        <Button
          icon={<Printer size={20} aria-hidden="true" />}
          variant="outline"
          disabled={label.isPending}
          onClick={() => {
            label.mutate({ shipmentId, use: 'print' });
          }}
        >
          {label.isPending ? t('orders.labelPreparing') : t('orders.labelPrint')}
        </Button>
        <Button
          icon={<Download size={20} aria-hidden="true" />}
          variant="ghost"
          disabled={label.isPending}
          onClick={() => {
            label.mutate({ shipmentId, use: 'download' });
          }}
        >
          {t('orders.labelDownload')}
        </Button>
      </span>
      {label.isError ? (
        <p role="status" className={styles['muted']}>
          {failure}
        </p>
      ) : null}
      {senderMissing ? (
        <Button variant="ghost" onClick={onOpenSettings}>
          {t('orders.fillSender')}
        </Button>
      ) : null}
    </div>
  );
};
